import api from './api';
import { normalizeLanguage } from '../constants/Languages';

const normalizeSupportedLanguages = (supportedLanguages = []) => {
  const languages = supportedLanguages
    .map((language) => normalizeLanguage(language))
    .filter((language, index, array) => array.indexOf(language) === index);

  return languages.length > 0 ? languages : ['en'];
};

const normalizeLocalizedDictionary = (value) => {
  if (!value || typeof value !== 'object') {
    return {};
  }

  return Object.entries(value).reduce((accumulator, [key, entryValue]) => {
    if (typeof entryValue !== 'string') {
      return accumulator;
    }

    accumulator[normalizeLanguage(key)] = entryValue;
    return accumulator;
  }, {});
};

const buildLocalizedPayload = (value) => (
  Object.fromEntries(
    Object.entries(normalizeLocalizedDictionary(value)).map(([language, entryValue]) => [language.toUpperCase(), entryValue.trim()])
  )
);

const toFormCategory = (category) => ({
  id: category?.id ?? null,
  name: normalizeLocalizedDictionary(category?.name),
  description: normalizeLocalizedDictionary(category?.description),
  isActive: category?.isActive !== false,
  sortOrder: category?.sortOrder ?? 0,
  serviceIds: Array.isArray(category?.serviceIds) ? [...category.serviceIds] : [],
});

const buildCategoryPayload = (category) => ({
  name: buildLocalizedPayload(category.name),
  description: buildLocalizedPayload(category.description),
  isActive: Boolean(category.isActive),
  serviceIds: Array.isArray(category.serviceIds)
    ? category.serviceIds.map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0)
    : [],
});

const ServiceCategoryService = {
  toFormCategory,

  getManagementData: async (storeId) => {
    const response = await api.get('/service-categories/management-data', { _storeId: storeId });
    const data = response.data;

    return {
      supportedLanguages: normalizeSupportedLanguages(data?.supportedLanguages),
      storeLanguage: normalizeLanguage(data?.storeLanguage || 'en'),
      currency: data?.currency || 'EUR',
      categories: Array.isArray(data?.categories)
        ? data.categories.map((category) => toFormCategory(category))
        : [],
      services: Array.isArray(data?.services)
        ? data.services.map((service) => ({
          id: service.id,
          name: normalizeLocalizedDictionary(service.name),
          disabled: Boolean(service.disabled),
        }))
        : [],
    };
  },

  getActiveCategories: async (storeId) => {
    const response = await api.get('/service-categories?activeOnly=true', { _storeId: storeId });
    return Array.isArray(response.data)
      ? response.data.map((category) => toFormCategory(category))
      : [];
  },

  createCategory: async (storeId, category) => {
    const response = await api.post('/service-categories', buildCategoryPayload(category), { _storeId: storeId });
    return toFormCategory(response.data);
  },

  updateCategory: async (storeId, categoryId, category) => {
    const response = await api.put(`/service-categories/${categoryId}`, buildCategoryPayload(category), { _storeId: storeId });
    return toFormCategory(response.data);
  },

  deleteCategory: async (storeId, categoryId) => {
    await api.delete(`/service-categories/${categoryId}`, { _storeId: storeId });
  },

  reorderCategories: async (storeId, orderedCategoryIds) => {
    await api.put('/service-categories/reorder', { orderedCategoryIds }, { _storeId: storeId });
  },
};

export default ServiceCategoryService;
